import { Star, MessageCircle } from "lucide-react";

const SOURCE_LABEL = { airbnb: "Airbnb", booking: "Booking", google: "Google", internal: "Direto" };

const Stars = ({ rating, size = 14 }) => {
  const full = Math.round(Number(rating));
  return (
    <span className="inline-flex items-center text-[#C5A059]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={`star-${i}`}
          size={size}
          strokeWidth={1.2}
          fill={i < full ? "#C5A059" : "transparent"}
        />
      ))}
    </span>
  );
};

const ReviewsPanel = ({ data }) => {
  const items = data?.items || [];
  const average = data?.average || 0;
  const count = data?.count || 0;

  if (!count) {
    return (
      <div className="bg-[#FCFAF8] border border-[#C5A059]/30 p-10 text-center">
        <MessageCircle size={28} className="mx-auto text-[#C5A059]" strokeWidth={1.2} />
        <div className="mt-4 font-serif-display text-xl text-[#0A1128]">Sem avaliações ainda</div>
        <p className="text-sm text-[#3A435E] mt-2">
          As avaliações dos seus hóspedes aparecerão aqui, com a pontuação média e os comentários completos.
        </p>
      </div>
    );
  }

  // Distribution
  const dist = [5, 4, 3, 2, 1].map((band) => {
    const cnt = items.filter((r) => Math.round(Number(r.rating)) === band).length;
    return { band, count: cnt, pct: count ? (cnt / count) * 100 : 0 };
  });

  return (
    <div className="space-y-6" data-testid="reviews-panel">
      {/* Header with average score */}
      <div className="bg-[#0A1128] text-[#F4F1EB] p-8 grid md:grid-cols-3 gap-6 items-center">
        <div>
          <div className="overline text-[#C5A059]">Pontuação média</div>
          <div className="font-serif-display text-6xl font-light mt-2">{average.toFixed(2)}</div>
          <Stars rating={average} size={16} />
          <p className="text-xs text-[#F4F1EB]/70 mt-2">{count} avaliações no total</p>
        </div>
        <div className="md:col-span-2 space-y-2">
          {dist.map((row) => (
            <div key={row.band} className="flex items-center gap-3 text-xs">
              <span className="w-6 text-[#F4F1EB]/80">{row.band}★</span>
              <div className="flex-1 h-1.5 bg-[#F4F1EB]/15">
                <div className="h-full bg-[#C5A059]" style={{ width: `${row.pct}%` }} />
              </div>
              <span className="w-8 text-right text-[#F4F1EB]/70">{row.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews list */}
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((r) => (
          <article
            key={r.id}
            className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 flex flex-col gap-3"
            data-testid={`review-card-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <Stars rating={r.rating} />
                <div className="font-medium text-[#0A1128] mt-1.5">{r.guest_name || "—"}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#7B86A4] mt-1">
                  {SOURCE_LABEL[r.source] || r.source} · {r.date}
                </div>
              </div>
              <span className="font-serif-display text-2xl text-[#C5A059]">{Number(r.rating).toFixed(1)}</span>
            </div>
            {r.comment && (
              <p className="text-sm text-[#3A435E] leading-relaxed italic">«{r.comment}»</p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
};

export default ReviewsPanel;
